import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiHelpers } from '../utils/api';
import TwoFactorSettings from './TwoFactorSettings';

// WP-2E — the "set up two-factor authentication" prompt shown at login.
//
// Every staff member without 2FA sees this, admins included (D-2E1). They may
// clear it with "Not now" three times. The third clear locks the account and
// signs them out on the spot (D-2E4); only an admin can free it, in Staff &
// Security. After that unlock there is no "Not now" button at all (D-2E2).
//
// There is no click-outside close and no X. The two buttons are the only ways
// out, because the whole point is that the choice gets made.

// Session flag so a "Not now" survives navigating around the app. It is not in
// localStorage on purpose: the prompt must come back at the next login.
export const TWOFA_PROMPT_DISMISSED_KEY = 'twofa_prompt_dismissed';

const markDismissedForThisSession = () => {
  try {
    sessionStorage.setItem(TWOFA_PROMPT_DISMISSED_KEY, '1');
  } catch (e) {
    // Private browsing or a locked-down browser. Nothing to do: the prompt
    // simply reappears on the next page, which is the safe way to fail.
  }
};

// onClose is owned by App.js on purpose. App.js switches the rest of the app off
// while this prompt is up, so if the prompt hid itself with its own state the
// app could be left switched off with nothing on screen (a dead app until the
// page was reloaded). One value now drives both.
const TwoFactorEnrolPrompt = ({ onClose = () => {} }) => {
  const { user, refreshUser, logout } = useAuth();
  const [settingUp, setSettingUp] = useState(false);
  // Which screen TwoFactorSettings is showing: 'status', 'setup' or
  // 'recovery-codes'. Used to hide Back once the recovery codes are on screen.
  const [stage, setStage] = useState('status');
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);

  // Keep the keyboard inside this box. App.js switches the rest of the app off
  // while the prompt is up, but not every browser obeys that, so Tab is caught
  // here as well. Without it a person could Tab straight past the prompt into
  // the sidebar and carry on as if it were not there.
  useEffect(() => {
    const box = dialogRef.current;
    if (!box) return undefined;
    const focusable = () => Array.from(box.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.offsetParent !== null);

    // Land on the box a person types in when there is one (the 6-digit code on
    // the QR screen). Otherwise the first focusable thing is the small "Copy
    // secret" icon, which is a surprising place to start and one wasted Tab.
    const startItems = focusable();
    const startHere = startItems.find((el) => el.tagName === 'INPUT') || startItems[0];
    if (startHere) startHere.focus();

    const onKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (!box.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [settingUp, stage]);

  const mandatory = Boolean(user?.twofa_setup_mandatory);
  // The server counts down. Anything missing is treated as the last reminder,
  // so a stale screen can never hand out extra chances.
  const left = Number.isFinite(user?.twofa_dismissals_left)
    ? user.twofa_dismissals_left
    : 1;

  const notNowLabel = () => {
    if (left >= 3) return 'Not now (2 reminders left after this)';
    if (left === 2) return 'Not now (1 reminder left after this)';
    return 'Not now. This is your last reminder. Clearing it will lock your account until an admin unlocks it.';
  };

  const lastChance = left <= 1;

  // Two different reasons make set-up compulsory, and they must not be told the
  // same story. After a real unlock the server puts the reminder count back to
  // zero, so three reminders are left. The last-admin hold leaves the count at
  // three, so none are left: nobody unlocked them, they were never locked.
  const lastAdminHold = mandatory && left === 0;

  const handleNotNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await apiHelpers.dismiss2FASetup();
      const data = response?.data || {};
      if (data.locked) {
        // D-2E4: the third clear signs them out straight away. The message
        // stays on the login screen so they know what to ask the admin for.
        logout(data.message || 'Your account is locked. Ask an admin to unlock it in Staff & Security.');
        return;
      }
      if (data.last_admin) {
        // WP-2E round 3: only an admin can clear a 2FA lock, so the last admin
        // is never locked. Set-up is compulsory for them instead. Keep the
        // prompt up and re-read the user so the "Not now" button goes away.
        toast.error(data.message
          || 'You are the only admin left, so two-factor set-up is now compulsory for you.');
        try {
          await refreshUser();
        } catch (e) {
          // The server already saved it; the next page load reads the truth.
        }
        setBusy(false);
        return;
      }
      markDismissedForThisSession();
      onClose();
      try {
        await refreshUser();
      } catch (e) {
        // The count is already saved on the server; a stale count on screen
        // does no harm because the prompt is closed for this session anyway.
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 423) {
        // Already locked (a second tab got there first). Same outcome.
        logout(typeof detail === 'string'
          ? detail
          : 'Your account is locked. Ask an admin to unlock it in Staff & Security.');
        return;
      }
      if (status === 403) {
        // An admin unlocked the account: set-up is compulsory now. Re-read the
        // user so the "Not now" button disappears.
        toast.error(typeof detail === 'string' ? detail : 'Set-up must be completed before you can continue');
        try {
          await refreshUser();
        } catch (e) {
          // Leave the prompt as it is; the next page load will catch up.
        }
        setBusy(false);
        return;
      }
      if (status === 400) {
        // 2FA is already on (set up in another tab). Nothing to prompt for.
        onClose();
        try {
          await refreshUser();
        } catch (e) {
          // Same as above.
        }
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Could not save that. Please try again.');
      setBusy(false);
    }
  };

  const handleEnabled = async () => {
    // Close first. The prompt and the "rest of the app is switched off" flag are
    // the same value now, so this can never leave a dead screen behind, even if
    // the re-read below fails or hangs.
    onClose();
    try {
      await refreshUser();
    } catch (e) {
      // If the re-read fails nothing is lost: 2FA is on, and the next page load
      // reads the truth from the server.
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-gray-900/90 backdrop-blur-sm flex items-start sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="twofa-enrol-title"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-6 space-y-6"
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 flex-shrink-0 bg-yellow-400/20 rounded-full flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-yellow-400" />
          </div>
          <div>
            <h2 id="twofa-enrol-title" className="text-xl font-bold text-white">
              Protect your account with two-factor authentication
            </h2>
            <p className="text-sm text-gray-300 mt-2">
              You will need an authenticator app on your phone (Google Authenticator,
              Microsoft Authenticator or similar). It takes about two minutes.
            </p>
          </div>
        </div>

        {settingUp ? (
          <div className="space-y-3">
            <TwoFactorSettings embedded autoStart onEnabled={handleEnabled} onStage={setStage} />
            {/* A way back for someone who pressed Set up now and then found the
                phone was not with them. D-2E2: no way back once set-up is
                compulsory. It also goes once the recovery codes are on screen:
                by then 2FA is already on, and Back would throw the codes away. */}
            {!mandatory && stage !== 'recovery-codes' && (
              <button
                type="button"
                onClick={() => setSettingUp(false)}
                title="Go back to the Set up now and Not now choice"
                aria-label="Go back to the Set up now and Not now choice"
                className="text-sm text-gray-400 hover:text-white underline"
                data-testid="twofa-prompt-back"
              >
                Back
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {mandatory && (
              <p className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                {lastAdminHold
                  ? 'You are the only admin who can log in, so your account was not locked. Set-up must be completed before you can continue.'
                  : 'An admin unlocked your account. Set-up must be completed before you can continue.'}
              </p>
            )}

            <button
              type="button"
              onClick={() => setSettingUp(true)}
              disabled={busy}
              className="w-full misty-button misty-button-primary py-3 text-base font-semibold disabled:opacity-50"
              title={busy
                ? 'Saving your choice, one moment'
                : 'Set up now: start setting up two-factor authentication'}
              aria-label={busy
                ? 'Set up now. Saving your choice, one moment.'
                : 'Set up now: start setting up two-factor authentication'}
              data-testid="twofa-prompt-setup"
            >
              Set up now
            </button>

            {/* D-2E2: no way past this screen once an admin has unlocked them. */}
            {!mandatory && (
              <button
                type="button"
                onClick={handleNotNow}
                disabled={busy}
                className={`w-full misty-button py-3 text-sm disabled:opacity-50 ${
                  lastChance
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
                title={notNowLabel()}
                aria-label={notNowLabel()}
                data-testid="twofa-prompt-not-now"
              >
                {busy ? 'Saving...' : notNowLabel()}
              </button>
            )}
          </div>
        )}

        {/* Always a way out of the session. Someone whose set-up is compulsory
            and who has left their phone at home would otherwise be stuck on this
            screen: the sidebar Sign out is underneath this cover and switched
            off. Signing out is not a way past set-up, the same screen is waiting
            at their next login, but it does free the computer for a colleague. */}
        <div className="pt-2 border-t border-gray-700 text-center">
          <button
            type="button"
            onClick={() => logout()}
            title="Sign out and go back to the login screen"
            aria-label="Sign out and go back to the login screen"
            className="text-sm text-gray-400 hover:text-white underline"
            data-testid="twofa-prompt-sign-out"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorEnrolPrompt;
